import { Injectable } from '@angular/core';

/**
 * Disambiguates the two sidebar entries that can point at the SAME route.
 *
 * When an org pins a default landing page (organisation.default_landing_page)
 * the menu grows an "Overview" shortcut at the top, while the page also keeps
 * its normal entry inside its own section (e.g. Attendance > Employee Work
 * Status). Both then match the current URL, so URL-based highlighting lights
 * BOTH and the user cannot tell which one they are on.
 *
 * The URL alone cannot resolve this — the two entries are the same route — so
 * the tie-break has to remember HOW the user got there:
 *   - no explicit choice (fresh login, refresh, or clicking Overview) -> Overview
 *   - the user clicked the in-section entry                            -> that entry
 * Navigating anywhere else clears the choice, so the next arrival via the
 * landing/Overview focuses Overview again.
 *
 * When no landing is pinned, `overviewLink` stays null and every consumer
 * short-circuits — the sidebar behaves exactly as it did before this existed.
 */
@Injectable({ providedIn: 'root' })
export class MenuFocusService {
  // The pinned landing link, i.e. the ONE link that can appear twice in the
  // sidebar. Null when the org pins nothing (the overwhelmingly common case).
  overviewLink: string | null = null;

  // Which of the twins the user last chose. Null = no explicit choice yet.
  private choice: 'overview' | 'inner' | null = null;

  setOverviewLink(link: string | null): void {
    if (this.overviewLink !== link) {
      this.overviewLink = link;
      this.choice = null;
    }
  }

  // Called when a sidebar leaf is clicked. Clicking either twin records which
  // one; clicking anything else clears the choice.
  select(link: string, isOverview: boolean): void {
    if (this.overviewLink && link === this.overviewLink) {
      this.choice = isOverview ? 'overview' : 'inner';
      return;
    }
    this.choice = null;
  }

  // Reset when the user navigates off the pinned route entirely.
  syncToUrl(currentUrl: string): void {
    if (!this.overviewLink || currentUrl !== this.overviewLink) {
      this.choice = null;
    }
  }

  // Should this twin render as focused? Only ever consulted for items whose
  // link IS the pinned landing. Default (no choice) = the Overview shortcut.
  isFocusedTwin(isOverview: boolean): boolean {
    if (this.choice === null) {
      return isOverview;
    }
    return this.choice === 'overview' ? isOverview : !isOverview;
  }
}
